import {
  useCan,
  useCustomMutation,
  useInvalidate,
  useList,
  useNotification,
  useTranslate,
} from '@refinedev/core';
import { Button, Modal } from 'antd';
import type { OAuthTokenOut } from '@/api/generated/schemas';

const RESOURCE = 'iam.oauth_tokens';

/** Revokes every active OAuth token of a user after a confirmation modal.
 * Disabled while the user has no active tokens. */
export const RevokeAllOAuthTokensButton = ({ userId }: { userId: number }) => {
  const t = useTranslate();
  const { open } = useNotification();
  const invalidate = useInvalidate();
  const { mutateAsync, mutation } = useCustomMutation();

  const { data: canRevokeAll } = useCan({
    resource: RESOURCE,
    action: 'revoke_all',
    params: { id: userId },
  });

  // Only the total matters, so fetch a single row.
  const { result: activeTokens, query } = useList<OAuthTokenOut>({
    resource: RESOURCE,
    filters: [
      { field: 'user_id', operator: 'eq', value: userId },
      { field: 'revoked', operator: 'eq', value: false },
    ],
    pagination: { pageSize: 1 },
  });
  const activeCount = activeTokens?.total ?? 0;

  if (!canRevokeAll?.can) return null;

  const handleClick = () =>
    Modal.confirm({
      title: t('iam.oauth_tokens.actions.revoke_all_confirm_title'),
      content: t('iam.oauth_tokens.actions.revoke_all_confirm_description', {
        count: activeCount,
      }),
      okText: t('iam.oauth_tokens.actions.revoke_all'),
      okButtonProps: { danger: true },
      cancelText: t('buttons.cancel'),
      onOk: async () => {
        try {
          await mutateAsync({
            url: `iam/oauth_tokens/${userId}/revoke_all`,
            method: 'patch',
            values: {},
          });
        } catch {
          // Error notification is shown by refine.
          return;
        }
        open?.({
          type: 'success',
          message: t('notifications.success'),
          description: t('iam.oauth_tokens.actions.revoke_all_success'),
        });
        invalidate({ resource: RESOURCE, invalidates: ['list'] });
      },
    });

  return (
    <Button
      danger
      size="small"
      disabled={activeCount === 0}
      loading={query.isLoading || mutation.isPending}
      onClick={handleClick}
    >
      {t('iam.oauth_tokens.actions.revoke_all')}
    </Button>
  );
};
